import { BaseApiService } from './BaseApiService';

export interface ProductGroup {
  product_group_id: number;
  name: string;
  sort_order: number;
  created_at: string;
  product_count: number;
  display_label: string;
}

export interface ProductGroupTableRow {
  product_id: number;
  product_code: string;
  name: string;
  product_group_id: number;
  product_group_name: string;
  sort_order: number;
}

export interface ProductGroupPage {
  rows: ProductGroupTableRow[];
  total: number;
  page: number;
  page_size: number;
}

export interface NeedsResolutionRow {
  item_code: string;
  description: string;
  product_group_name: string;
  product_group_sort_order: number;
  product_group_display_label: string;
  file_row: number;
}

export interface DuplicateItemWarning {
  item_code: string;
  description: string;
  kept_product_group_name: string;
  kept_sort_order: number;
  kept_display_label: string;
  dropped_product_group_name: string;
  dropped_sort_order: number;
  dropped_display_label: string;
  file_row: number;
}

export interface ReturningToFileRow {
  product_id: number;
  product_code: string;
  product_name: string;
  current_product_group_name: string;
  current_display_label: string;
  file_product_group_name: string;
  file_display_label: string;
}

export interface RemovedFromGroupsRow {
  product_id: number;
  product_code: string;
  product_name: string;
  current_product_group_name: string;
  current_display_label: string;
}

export interface ProductGroupImportPreview {
  product_group_count: number;
  matched_product_count: number;
  needs_resolution: NeedsResolutionRow[];
  duplicate_warnings: DuplicateItemWarning[];
  parse_warnings: string[];
  returning_to_file: ReturningToFileRow[];
  removed: RemovedFromGroupsRow[];
  parse_error: string | null;
  catalog_version: string;
  applied: boolean;
}

class ProductGroupService extends BaseApiService {
  async list_groups(): Promise<ProductGroup[]> {
    const response = await this.get<{ success: boolean; data: ProductGroup[] }>(
      '/products/groups'
    );
    return response.data || [];
  }

  async list_product_page(page: number, page_size: number, search: string): Promise<ProductGroupPage> {
    const response = await this.get<{ success: boolean; data: ProductGroupPage }>(
      '/products/groups/products',
      { page, page_size, search }
    );
    if (!response.data) throw new Error('Failed to list products');
    return response.data;
  }

  async remove_product(product_id: number): Promise<void> {
    await this.delete<{ success: boolean }>(`/products/groups/products/${product_id}`);
  }

  async delete_group(product_group_id: number): Promise<void> {
    await this.delete<{ success: boolean }>(`/products/groups/${product_group_id}`);
  }

  async create_group(name: string): Promise<ProductGroup> {
    const response = await this.post<{ success: boolean; data: ProductGroup }>(
      '/products/groups',
      { name }
    );
    if (!response.data) throw new Error('Failed to create product group');
    return response.data;
  }

  async move_products(
    product_ids: number[],
    destination_product_group_id: number
  ): Promise<{ moved_count: number }> {
    const response = await this.post<{ success: boolean; data: { moved_count: number } }>(
      '/products/groups/move',
      { product_ids, destination_product_group_id }
    );
    if (!response.data) throw new Error('Failed to move products');
    return response.data;
  }

  async import_file(
    file_base64: string,
    file_name: string,
    apply: boolean,
    catalog_version = ''
  ): Promise<ProductGroupImportPreview> {
    const response = await this.post<{ success: boolean; data: ProductGroupImportPreview }>(
      '/products/groups/import',
      { file_base64, file_name, apply, catalog_version }
    );
    if (!response.data) throw new Error('Failed to import product groups');
    return response.data;
  }
}

export const productGroupService = new ProductGroupService();
